import { DatePipe } from '@angular/common';
import { NgZone } from '@angular/core';
import { BLANK_TYPE } from 'document-type-picker-lib';
import { IAttachmentMergeFile, IIconService, INamedEntity } from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { AutoGenIconPipe, TransformBytesToMB } from 'pipes-lib';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { ContentTypeMapperService, FileExtensionMatcherService } from 'utils-service-lib';
import { runtimeEnvironment } from '../../../../../environments/environment';
import { ISimpleTableRow } from '../../../../common-components/simple-table/simple-table.interfaces';
import { DocumentClientDatasourceApiListServiceFactory } from '../../../../services/datasource/document-client-datasource-list.service';
import { ServiceProviderService } from '../../../../services/service.provider.service';
import { IAttachFileDialogDataSingleEntity } from '../datasource/datasource-attach-file.component';

// do we need this/has it moved?
export interface IAttachFileTableRowData {
  id: string;
  name: string;
  type: string;
  size?: number;
  dateModified?: number;
  fileExtension?: string;
  contentSubType?: string;
}

export abstract class AttachFileTableBaseComponent {
  protected constructor(
    protected messageNotificationService: MessageNotificationService,
    protected transformBytesToMB: TransformBytesToMB,
    protected datePipe: DatePipe,
    protected ngZone: NgZone,
    protected serviceProviderService: ServiceProviderService,
    protected sharedServiceProviderServiceFactory: SharedServiceProviderServiceFactory,
    protected identityStorage: IdentityStorage,
    protected fileExtensionMatcherService: FileExtensionMatcherService,
    protected contentTypeMapperService: ContentTypeMapperService,
  ) {}

  protected static fileSort(a: ISimpleTableRow, b: ISimpleTableRow): number {
    return a.data.name.localeCompare(b.data.name);
  }
  renderMode = runtimeEnvironment.renderMode;
  files: ISimpleTableRow[] = [];
  compactFileListViewFiles: IAttachmentMergeFile[] = [];
  showSpinner = false;
  protected iconService: IIconService;

  protected updateUi = runtimeEnvironment.runningAsChromeExtension
    ? (update: () => void) => this.ngZone.run(() => update())
    : (update: () => void) => update();

  protected abstract onFilesLoaded(files: ISimpleTableRow[], data: IAttachFileDialogDataSingleEntity): void;

  protected buildTable(entity: INamedEntity, data: IAttachFileDialogDataSingleEntity): void {
    this.iconService = this.serviceProviderService.getIconService(data.templateNamespace);
    this.updateUi(() => (this.showSpinner = true));

    DocumentClientDatasourceApiListServiceFactory.instance
      .create(runtimeEnvironment.origin.namespace, data.fileListProvider.dataSourceApiService)
      .list({ entity, associatedType: data.fileListProvider.listType })
      .subscribe(
        (resp) => {
          const normalisedFiles = resp.map((file) => this.contentTypeMapperService.normaliseFileExtension(file));

          const filters = data.sectionConfig.filters;
          if (data.useFileAttachmentAsTemplate) {
            filters.extension = ['.docx']; // enforce .docx extension when using attachment as template
          }
          let keep = normalisedFiles.filter((file) => {
            return this.fileExtensionMatcherService.matchesAllowedExtension(file, filters.extension);
          });

          if (filters.documentTypeId) {
            keep = keep.filter((f) => {
              const hasBlank = filters.documentTypeId.includes(BLANK_TYPE.id);
              return hasBlank
                ? !f.documentType?.id || f.documentType.id === '' || filters.documentTypeId.find((dtId) => f.documentType?.id === dtId)
                : filters.documentTypeId.find((dtId) => f.documentType?.id === dtId);
            });
          }

          // filter by max age
          if (filters.fileMaxAgeDays && filters.fileMaxAgeDays > 0) {
            keep = keep.filter((f) => f.dateAdded >= this.dateFilterToMillis(filters.fileMaxAgeDays));
          }

          let tableRows: ISimpleTableRow[] = [];
          for (const file of keep) {
            const fileData = {
              path: [runtimeEnvironment.origin.namespace, entity.type, entity.id, file.id],
              id: file.id,
              name: file.name,
              type: file.documentType?.label || file.type || '', // salesforce using 'type' field
              size: file.size,
              dateModified: file.dateModified,
              fileExtension: file.fileExtension,
              contentSubType: file.contentSubType,
              downloadUrl: file.downloadUrl, // important for salesforce
              origin: file.origin, // important for salesforce
              contentType: file.contentType,
            };
            tableRows.push(this.buildTableRow(<IAttachmentMergeFile>fileData));
            this.compactFileListViewFiles.push(<IAttachmentMergeFile>fileData);
          }

          this.updateUi(() => {
            this.files = tableRows.sort(AttachFileTableBaseComponent.fileSort);
            this.showSpinner = false;

            this.onFilesLoaded(this.files, data);
          });
        },
        (error) => {
          this.updateUi(() => (this.showSpinner = false));
          this.messageNotificationService.error('Error getting file list.', error);
        },
      );
  }

  protected buildTableRow(file: IAttachmentMergeFile): ISimpleTableRow {
    const cells =
      this.renderMode === 'web-app'
        ? [
            { autoIcon: AutoGenIconPipe.AUTO_GEN_ICON, text: file.name, fileExtension: file.fileExtension },
            { text: file.type },
            { text: this.transformBytesToMB.transform(file.size) },
            { text: this.datePipe.transform(file.dateModified, 'medium') },
          ]
        : [
            { autoIcon: AutoGenIconPipe.AUTO_GEN_ICON, text: file.name, fileExtension: file.fileExtension },
            { text: this.datePipe.transform(file.dateModified, 'medium') },
          ];

    return { cells, data: { ...file } };
  }

  private dateFilterToMillis(days: number): string {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    now.setDate(now.getDate() - days);
    return now.getTime().toString();
  }
}
